import { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { isDesktop } from '../lib/api';
import { setDesktopToken } from '../lib/desktopToken';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { getStaffLandingPath } from '../lib/staffLanding';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const platform = window.location.pathname.startsWith('/platform');
    const processCallback = async () => {
      try {
        const hash = location.hash || window.location.hash;
        const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

        if (!sessionId) {
          navigate(platform ? '/platform' : '/login', { replace: true });
          return;
        }

        const res = await api.post('/auth/session', { session_id: sessionId });
        const { token, user: userData } = res.data;
        // Browser web app now authenticates via the httpOnly `session_token`
        // cookie the backend just set; only the desktop client still needs
        // the Bearer token in localStorage (see lib/api.js::isDesktop).
        if (token && isDesktop()) {
          setDesktopToken(token);
        }
        if (userData) {
          localStorage.setItem('pharmxai_user', JSON.stringify(userData));
          setAuthUser(userData);
        }
        navigate(platform ? '/platform' : getStaffLandingPath(userData), { replace: true, state: { user: userData } });
      } catch (error) {
        // Surface allowlist / policy denials so the user knows why sign-in
        // was refused — otherwise a 403 looks like a silent redirect bug.
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (status === 403 && detail) {
          toast.error(detail, { duration: 8000 });
        } else if (status && status !== 401) {
          toast.error(detail || 'Sign-in failed. Please try again.');
        }
        console.error('Auth callback error:', error);
        navigate(platform ? '/platform' : '/login', { replace: true });
      }
    };

    processCallback();
  }, [location, navigate, setAuthUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
